import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, Loader2, Save } from "lucide-react";
import axios from "axios";
import { API, apiClient } from "@/lib/auth";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();

export default function DemoSavingsLink({ demo, briefs, onSaved }) {
  const [briefId, setBriefId] = useState(demo.brief_id || "");
  const [override, setOverride] = useState(demo.savings_override ? String(demo.savings_override) : "");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPreview = () =>
    axios.get(`${API}/demos/${demo.slug}/nex-savings`).then(({ data }) => setPreview(data)).catch(() => setPreview(null));
  useEffect(() => { loadPreview(); }, [demo.slug, demo.brief_id, demo.savings_override]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/admin/demo-directory/${demo.id}`, {
        brief_id: briefId,
        savings_override: override ? parseInt(override, 10) : 0,
      });
      toast.success("Savings settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Failed to save");
    } finally { setSaving(false); }
  };

  const b = preview?.breakdown;
  return (
    <div data-testid={`demo-savings-${demo.slug}`} className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.16em] uppercase text-emerald-300 mb-2">
        <Calculator className="w-3.5 h-3.5" /> The Exchange Alliance savings tile
        <span className="ml-auto text-emerald-200 text-xs tracking-normal" data-testid={`demo-savings-total-${demo.slug}`}>
          {preview ? `${money(preview.annual)} / yr` : "—"}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2">
        <select
          value={briefId}
          onChange={(e) => setBriefId(e.target.value)}
          data-testid={`demo-savings-brief-${demo.slug}`}
          className="rounded-lg bg-navy-950 border border-white/10 focus:border-emerald-400 focus:outline-none px-3 py-2 text-sm text-white"
        >
          <option value="">No friction audit linked</option>
          {briefs.map((x) => (
            <option key={x.id} value={x.id}>
              {`${x.business_name} — ${x.full_name || x.email} · ${(x.submitted_at || "").slice(0, 10)}`}
            </option>
          ))}
        </select>
        <input
          value={override}
          onChange={(e) => setOverride(e.target.value.replace(/\D/g, "").slice(0, 9))}
          placeholder="Override $ / yr (optional)"
          inputMode="numeric"
          data-testid={`demo-savings-override-${demo.slug}`}
          className="rounded-lg bg-navy-950 border border-white/10 focus:border-emerald-400 focus:outline-none px-3 py-2 text-sm text-white font-mono"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid={`demo-savings-save-${demo.slug}`}
          className="inline-flex items-center justify-center gap-1 bg-emerald-500/90 hover:bg-emerald-400 text-navy-950 font-mono text-xs font-semibold tracking-[0.14em] uppercase px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
      {b && (
        <div className="mt-2 font-mono text-[11px] text-slate-400" data-testid={`demo-savings-breakdown-${demo.slug}`}>
          {b.hours_per_week} hrs/wk × ${preview.labor_rate}/hr × 52 = {money(b.labor_annual)} labour
          {" + "}{b.tools_replaced.length} tools × $600 = {money(b.tools_annual)}
          {" → "}<span className="text-slate-200">{money(b.computed_annual)}</span>
          {preview.override ? <span className="text-amber-300"> · overridden to {money(preview.override)}</span> : null}
        </div>
      )}
    </div>
  );
}
